<template>
  <div class="submit-story-page">
    <div class="container">
      <h1>{{ isEdit ? "Edit Story" : "Write & Submit a Story" }}</h1>

      <!-- Title + Category + Word Count -->
      <div class="meta-row">
        <input
          v-model="title"
          type="text"
          placeholder="Enter story title"
          class="title-input"
        />

        <!-- Main Category -->
        <select v-model="genre" class="category-select">
          <option value="">Select Main Category</option>
          <option value="poetry">Poetry (Sayari)</option>
          <option value="poem">Poem</option>
          <option value="Short Story">Short Story</option>
          <option value="Long Story">Long Story</option>
        </select>

        <!-- Subcategory -->
        <select
          v-if="['Short Story', 'Long Story'].includes(genre)"
          v-model="subcategory"
          class="category-select"
        >
          <option value="">Select Subcategory</option>
          <option v-for="opt in availableSubcategories" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>

        <div class="wordcount">Words: {{ wordCount }}</div>
      </div>

      <!-- Content -->
      <textarea
        v-model="content"
        placeholder="Write your story here..."
        class="content-area"
      ></textarea>

      <!-- Policy Check -->
      <div class="controls">
        <label class="checkbox">
          <input type="checkbox" v-model="agreePolicy" />
          I confirm this is original work.
        </label>
      </div>

      <!-- Buttons -->
      <div class="buttons">
        <button class="btn draft" @click="saveDraft">
          {{ isEdit ? "Save Draft" : "Save as Draft" }}
        </button>
        <button class="btn publish" @click="publish">
          {{ isEdit ? "Update & Publish" : "Publish" }}
        </button>
        <button class="btn clear" @click="clearAll">Clear</button>
      </div>
    </div>

    <!-- Ad Popup -->
    <div v-if="showAd" class="ad-popup-overlay">
      <div class="ad-popup">
        <button class="close-btn" @click="closeAd">×</button>
        <AdBanner position="between_posts" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { supabase } from "@/supabase";
import SHA256 from "crypto-js/sha256";
import AdBanner from "@/components/AdBanner.vue";

const router = useRouter();
const route = useRoute();

const title = ref("");
const content = ref("");
const genre = ref("");
const subcategory = ref("");
const availableSubcategories = ref([]);
const agreePolicy = ref(false);
const userId = ref(null);
const userEmail = ref(null);
const showAd = ref(false);
const isEdit = ref(false);

/* ✅ CHANGE 1: Draft ID holder */
const storyId = ref(null);

// 🧠 Detect and Load Draft if editing
onMounted(async () => {
  const { data } = await supabase.auth.getSession();
  userId.value = data?.session?.user?.id;
  userEmail.value = data?.session?.user?.user_metadata?.email || data?.session?.user?.email;

  const draftId = route.query.draftId;
  if (draftId) {
    isEdit.value = true;
    await loadDraft(draftId);
  }
});

// ✅ Function to load existing draft
async function loadDraft(draftId) {
  try {
    /* ✅ CHANGE 2: Store draftId */
    storyId.value = draftId;

    const { data, error } = await supabase
      .from("stories")
      .select("title, content, genre, subcategory, status")
      .eq("id", draftId)
      .single();

    if (error) throw error;

    if (data) {
      title.value = data.title || "";
      content.value = data.content || "";
      genre.value = data.genre || "";
      subcategory.value = data.subcategory || "";
    }
  } catch (err) {
    console.error("Error loading draft:", err.message);
    alert("⚠️ Unable to load draft. Please try again.");
  }
}

// Subcategory handling
watch(genre, (newVal) => {
  if (newVal === "Short Story") {
    availableSubcategories.value = [
      
      "Love",
      "Romantic",
      "Emotional",
      "Drama",
      "Family",
      "Friendship",
      "Inspirational",
      "Social Issue Based",
      "Real Life Based",
      "Moral Story",
      "Children",
      "Horror",
      "Thriller",
      "Mystery",
      "Crime",
      "Suspense",
      "Psychological",
      "Dark",
      "Tragic",
      "Fiction",
      "Fantasy",
      "Science Fiction",
      "Adventure",
      "Mythological",
      "Historical",
      "Spiritual",
      "Philosophical",
      "Comedy",
      "Character Driven",
      "Plot Driven",
      "Twist Ending",
      "Open Ending"
    ];
  } else if (newVal === "Long Story") {
    availableSubcategories.value = [

      "Love",
      "Romantic",
      "Emotional",
      "Drama",
      "Family",
      "Friendship",
      "Inspirational",
      "Social Issue Based",
      "Real Life Based",
      "Moral Story",
      "Children",
      "Horror",
      "Thriller",
      "Mystery",
      "Crime",
      "Suspense",
      "Psychological",
      "Dark",
      "Tragic",
      "Fiction",
      "Fantasy",
      "Science Fiction",
      "Adventure",
      "Mythological",
      "Historical",
      "Spiritual",
      "Philosophical",
      "Comedy",
      "Character Driven",
      "Plot Driven",
      "Twist Ending",
      "Open Ending"
    ];
  } else {
    availableSubcategories.value = [];
    subcategory.value = "";
  }
});

const wordCount = computed(() =>
  content.value.trim() ? content.value.trim().split(/\s+/).length : 0
);

const computeHash = (txt) =>
  SHA256(txt.trim().replace(/\s+/g, " ").toLowerCase()).toString();

  const closeAd = () => {
  showAd.value = false;

  // ✅ correct profile route
  router.push(`/profile/${userId.value}`);
};

// ✅ Save Draft (STABLE VERSION – ONLY MINIMAL FIX)
const saveDraft = async () => {
  if (!userId.value) return alert("Please login first.");
  if (!title.value.trim()) return alert("Please enter a title.");
  if (!genre.value) return alert("Please select a category.");

  const hash = computeHash(content.value);
  const payload = {
    author_id: userId.value,
    email: userEmail.value,
    title: title.value.trim(),
    content: content.value.trim(),
    content_hash: hash,
    genre: genre.value,
    subcategory: subcategory.value || null,
    status: "draft",
    is_approved: false,
  };

  try {
    /* ✅ CHANGE 3: INSERT captures draft ID */
    if (storyId.value) {
      const { error } = await supabase
        .from("stories")
        .update(payload)
        .eq("id", storyId.value);

      if (error) throw error;
    } else {
      const { data, error } = await supabase
        .from("stories")
        .insert(payload)
        .select("id")
        .single();

      if (error) throw error;

      storyId.value = data.id;
      isEdit.value = true;
    }

    alert(isEdit.value ? "✅ Draft updated successfully!" : "✅ Draft saved successfully!");
    showAd.value = true;
    setTimeout(() => closeAd(), 5000);

  } catch (err) {
    console.error("Draft Error:", err);
    alert("❌ Error saving draft: " + err.message);
  }
};

/* ----------------------------- */
/* ✅ PUBLISH STORY (UNCHANGED) */
/* ----------------------------- */
const publish = async () => {
  if (!agreePolicy.value)
    return alert("Please accept the declaration policy first.");
  if (!genre.value) return alert("Please select a main category.");

  let minWords = 0;
  if (genre.value === "Poetry (Sayari)" || genre.value === "poetry") minWords = 30;
  else if (genre.value === "Poem" || genre.value === "poem") minWords = 70;
  else if (genre.value === "Short Story") minWords = 500;
  else if (genre.value === "Long Story") minWords = 1000;

  if (wordCount.value < minWords)
    return alert(
      `❌ ${genre.value} requires at least ${minWords} words. You have ${wordCount.value}.`
    );

  const hash = computeHash(content.value);

  const { data: existingStories, error: fetchError } = await supabase
    .from("stories")
    .select("id, title, content")
    .eq("status", "published");

  if (fetchError) return alert("Error checking plagiarism.");

  for (const story of existingStories) {
    const similarity = textSimilarity(content.value, story.content);
    if (similarity >= 70)
      return alert(
        `⚠️ Possible Duplicate Detected (${similarity}% similar to "${story.title}")`
      );
  }

  let tokenAmount = 0;
  if (genre.value === "Poetry (Sayari)" || genre.value === "poetry") tokenAmount = 0.1;
  else if (genre.value === "Poem" || genre.value === "poem") tokenAmount = 0.2;
  else if (genre.value === "Short Story") tokenAmount = 0.5;
  else if (genre.value === "Long Story") tokenAmount = 1.0;

  const payload = {
    author_id: userId.value,
    email: userEmail.value,
    title: title.value.trim(),
    content: content.value.trim(),
    content_hash: hash,
    genre: genre.value,
    subcategory: subcategory.value || null,
    status: "published",
    is_approved: true,
  };

  try {
    if (storyId.value) {
  // 👉 draft already exists → UPDATE
  const { error } = await supabase
    .from("stories")
    .update(payload)
    .eq("id", storyId.value);

  if (error) throw error;
} else {
  // 👉 no draft → INSERT new story
  const { error } = await supabase
    .from("stories")
    .insert({
      ...payload,
      author_id: userId.value,
      email: userEmail.value,
    });

  if (error) throw error;
}

    const { error: rpcError } = await supabase.rpc("reward_tokens", {
      user_id_input: userId.value,
      amount: parseFloat(tokenAmount.toFixed(2)),
    });

    if (rpcError) throw rpcError;


    alert(
      `🎉 ${genre.value} published successfully!\nYou earned ${tokenAmount} Mudra Tokens.`
    );

    showAd.value = true;
    setTimeout(() => {
      showAd.value = false;
      router.push("/stories");
    }, 4000);
  } catch (err) {
    console.error("Publish Error:", err);
    alert("❌ Error publishing story: " + err.message);
  }
};

// Similarity function
function textSimilarity(text1, text2) {
  if (!text1 || !text2) return 0;
  const words1 = text1.toLowerCase().split(/\s+/);
  const words2 = text2.toLowerCase().split(/\s+/);
  const set1 = new Set(words1);
  const set2 = new Set(words2);
  const intersection = [...set1].filter((w) => set2.has(w));
  const similarity = (intersection.length * 2) / (set1.size + set2.size);
  return Math.round(similarity * 100);
}

// Clear Form
const clearAll = () => {
  title.value = "";
  content.value = "";
  genre.value = "";
  subcategory.value = "";
  agreePolicy.value = false;
};
</script>

<style scoped>
.submit-story-page {
  min-height: calc(100vh - 80px);
  padding-top: 100px;
  background: linear-gradient(180deg, #010d1e 0%, #031c3a 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  color: #dce6f6;
  font-family: "Inter", sans-serif;
}

.container {
  width: 900px;
  max-width: 95%;
  background: rgba(0, 0, 0, 0.45);
  padding: 36px 40px;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
}

h1 {
  color: #00c8ff;
  font-size: 28px;
  text-align: center;
  margin-bottom: 24px;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
}

.title-input {
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid rgba(0, 200, 255, 0.3);
  background: #ffffff;
}

.wordcount {
  min-width: 140px;
  color: #bfeaff;
}

.content-area {
  width: 95%;
  min-height: 420px;
  padding: 20px 24px;
  margin: 16px 0 24px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 200, 255, 0.25);
  font-family: "Georgia", serif;
  line-height: 30px;
  color: #000;
}

.buttons {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
}

.btn {
  flex: 1;
  min-width: 160px;
  padding: 12px 16px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
}

.btn.draft {
  background: #5f5f5f;
  color: #fff;
}

.btn.publish {
  background: linear-gradient(90deg, #00c8ff, #0088ff);
  color: #000;
}

.btn.clear {
  background: #222;
  color: #fff;
}

/* ================================
   FINAL AD POPUP – CLEAN & PERFECT
================================ */

/* Overlay */
.ad-popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 9999;
}

/* Popup Card */
.ad-popup {
  position: relative;

  max-width: 300px;        /* 👈 desktop control */
  width: 88%;
  padding: 10px;

  background: rgba(0, 0, 0, 0.75);
  border: 1px solid rgba(0, 200, 255, 0.25);
  border-radius: 14px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Ad Image */
.ad-popup img {
  width: 100%;
  max-width: 260px;       /* 👈 IMAGE SIZE CONTROL */
  max-height: 38vh;       /* 👈 desktop safe */
  object-fit: contain;
  border-radius: 10px;
}

/* Close Button */
.close-btn {
  position: absolute;
  top: 6px;
  right: 8px;

  font-size: 18px;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
}

/* Mobile View */
@media (max-width: 768px) {
  .container {
    padding: 20px;
  }

  h1 {
    font-size: 22px;
    margin-bottom: 18px;
  }

  .meta-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
  }

  .title-input {
    width: 90%;
  }

  .wordcount {
    width: 90%;
    text-align: left;
    font-size: 14px;
  }

  .content-area {
    width: 90%;
    min-height: 380px;
    font-size: 16px;
    line-height: 28px;
    padding: 16px;
  }

  .buttons {
    flex-direction: column;
    gap: 10px;
  }

  .btn {
    width: 100%;
  }
}

.category-select {
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(0, 200, 255, 0.3);
  background: #ffffff;
  color: #000;
  font-weight: 600;
}
.ad-popup {
    max-width: 260px;
  }

  .ad-popup img {
    max-height: 45vh;
  }
</style>